import React, { useEffect, useRef } from 'react'

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'

let leafletPromise = null

function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  if (window.L) return Promise.resolve(window.L)
  if (leafletPromise) return leafletPromise

  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = LEAFLET_CSS
      document.head.appendChild(link)
    }
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L))
      existing.addEventListener('error', reject)
      if (window.L) resolve(window.L)
      return
    }
    const script = document.createElement('script')
    script.src = LEAFLET_JS
    script.async = true
    script.onload = () => resolve(window.L)
    script.onerror = reject
    document.body.appendChild(script)
  })
  return leafletPromise
}

/**
 * Mapa OSM con marcador. Si `editable`, un clic actualiza el punto.
 */
export default function IncidentLocationMap({
  lat,
  lng,
  height = 280,
  editable = false,
  onChange,
}) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const hasPoint = lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))

  useEffect(() => {
    let cancelled = false

    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current) return

        if (!mapRef.current) {
          const center = hasPoint ? [Number(lat), Number(lng)] : [42.126, -1.14]
          const map = L.map(containerRef.current).setView(center, hasPoint ? 15 : 10)
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
            maxZoom: 19,
          }).addTo(map)
          mapRef.current = map

          if (editable) {
            map.on('click', (e) => {
              if (typeof onChangeRef.current === 'function') {
                onChangeRef.current(e.latlng.lat, e.latlng.lng)
              }
            })
          }
        }

        const map = mapRef.current
        if (hasPoint) {
          const la = Number(lat)
          const ln = Number(lng)
          if (markerRef.current) {
            markerRef.current.setLatLng([la, ln])
          } else {
            markerRef.current = L.marker([la, ln]).addTo(map)
          }
          map.setView([la, ln], Math.max(map.getZoom(), 15))
        } else if (markerRef.current) {
          map.removeLayer(markerRef.current)
          markerRef.current = null
        }

        setTimeout(() => map.invalidateSize(), 50)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [lat, lng, hasPoint, editable])

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        markerRef.current = null
      }
    }
  }, [])

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 0,
        isolation: 'isolate',
        width: '100%',
        height,
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #E4E6EF',
        background: '#f3f6f9',
      }}
    >
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}

/** Parsea notes legacy `coords:lat,lng`. */
export function parseLegacyCoords(notes) {
  if (!notes || typeof notes !== 'string') return null
  const m = notes.match(/coords:\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i)
  if (!m) return null
  const la = Number(m[1])
  const ln = Number(m[2])
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null
  return { lat: la, lng: ln }
}

export function stripLegacyCoords(notes) {
  if (!notes || typeof notes !== 'string') return notes || ''
  return notes
    .replace(/(?:^|\n)\s*coords:\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*(?:\n|$)/gi, '\n')
    .replace(/^\s+|\s+$/g, '')
    .replace(/\n{3,}/g, '\n\n')
}
